
import { Globe } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import apiClient from '../Config/apiClient';
import DescriptionBanner from '../Components/DescriptionBanner';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../Config/api';
import AwarenessDashboardCard from '../Components/AwarenessDashboardCard';
import AwarenessByDepartment from '../Components/Awareness/AwarenessByDepartment';

// Alias per compatibilità con il form del modal
// (va dichiarato dopo la definizione di handleAddUser)

const Awareness = () => {

  const { t } = useTranslation();

  // Defensive translation helper to avoid rendering objects
  function safeT(key, fallback = '') {
    const value = t(key);
    if (typeof value === 'object' && value !== null) {
      return fallback || key;
    }
    return value;
  }

  function normalizeCompletion(rawValue) {
    if (rawValue === null || rawValue === undefined) {
      return 0;
    }
    if (typeof rawValue === 'number') {
      if (!Number.isFinite(rawValue)) return 0;
      return Math.max(0, Math.min(100, rawValue));
    }

    const cleaned = String(rawValue)
      .replace('%', '')
      .replace(',', '.')
      .trim();
    const parsed = parseFloat(cleaned);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(0, Math.min(100, parsed));
  }

  // --- State e handler per la gestione utenti ---
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [seatsInfo, setSeatsInfo] = useState({ awareness_enabled: false, awareness_seats: 0, used: 0, remaining: 0 });
  const [userForm, setUserForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    qualifica: '',
    reparto: '',
    is_vip: false,
    is_active: true,
    lingua: 'it',
    stato: 'Non iniziato'
  });
  const [uploadFileName, setUploadFileName] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null); // Stato per la modalità modifica
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Stato per il modal di modifica
  const [sendingActivationId, setSendingActivationId] = useState(null); // utente per cui si sta inviando l'email
  const [activationModal, setActivationModal] = useState(null); // { user, status, error_type, error_detail }

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
    fetchSeatsInfo();
  }, []);

  // Auto-hide error / success messages and scroll into view when set
  useEffect(() => {
    if (error) {
      const el = document.querySelector('.awareness-form-container');
      el?.scrollIntoView({ behavior: 'smooth' });
      const id = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [error]);

  useEffect(() => {
    if (success) {
      const el = document.querySelector('.awareness-form-container');
      el?.scrollIntoView({ behavior: 'smooth' });
      const id = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [success]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.awarenessUsers);
      setUserList(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      console.error('Error response:', err.response?.data);
      setError(t('awarenessPage.errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  async function fetchSeatsInfo() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.awarenessUsers + 'seats_info/');
      setSeatsInfo(response.data);
    } catch (err) {
      console.error('Error fetching seats info:', err);
    }
  }

  function handleFormChange(e) {
    const { name, value, type, checked } = e.target;
    setUserForm((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  }

  async function handleAddUser(e) {
    e.preventDefault();

    if (!userForm.nome || !userForm.cognome || !userForm.email) {
      return;
    }


    try {
      if (editingUser) {
        // Modalità modifica
        const response = await apiClient.put(API_ENDPOINTS.awarenessUserDetail(editingUser.id), userForm);

        setUserList((prev) => prev.map(u => u.id === editingUser.id ? response.data : u));
        setEditingUser(null);
        setSuccess(t('awarenessPage.userModifiedSuccess'));
      } else {
        // Controllo lato client: blocca se non ci sono posti rimanenti
        if (seatsInfo.awareness_enabled && seatsInfo.remaining <= 0) {
          setError(t('awarenessPage.seatsLimitReached'));
          return;
        }
        // Modalità creazione
        const response = await apiClient.post(API_ENDPOINTS.awarenessUsers, userForm);

        setUserList((prev) => [...prev, response.data]);
        setSuccess(t('awarenessPage.userAddedSuccess'));
      }

      setUserForm({
        nome: '',
        cognome: '',
        email: '',
        qualifica: '',
        reparto: '',
        is_vip: false,
        is_active: true,
        lingua: 'it',
        stato: 'Non iniziato'
      });
      setEditingUser(null);
      setIsEditModalOpen(false);
      setError('');
    } catch (err) {
      console.error('Error saving user:', err);
      console.error('Error response:', err.response?.data);
      // mostriamo il messaggio del backend se presente (es. limite posti raggiunto)
      const data = err.response?.data;
      let message = editingUser ? t('awarenessPage.errorModifyingUser') : t('awarenessPage.errorAddingUser');
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (data.ordine) {
          message = Array.isArray(data.ordine) ? data.ordine[0] : data.ordine;
        } else if (data.detail) {
          message = data.detail;
        }
      }
      if (message && message.toLowerCase().includes('limite utenti awareness')) {
        message = t('awarenessPage.seatsLimitReachedShort');
      }
      setError(message);
    }
  }

  // Handler per il submit del form nel modal (poniamolo qui nello scope del componente)
  const handleFormSubmit = (e) => {
    handleAddUser(e);
  };

  async function handleDeleteUser(id) {
    try {
      await apiClient.delete(API_ENDPOINTS.awarenessUserDetail(id));
      setUserList((prev) => prev.filter(u => u.id !== id));
      setError('');
    } catch (err) {
      setError(t('awarenessPage.errorDeletingUser'));
      console.error('Error deleting user:', err);
    }
  }

  async function handleSendActivationEmail(u) {
    setSendingActivationId(u.id);
    try {
      const response = await apiClient.post(API_ENDPOINTS.awarenessSendActivationEmail(u.id));
      const data = response.data || {};
      // Aggiorna lo stato locale dell'utente con l'esito
      setUserList((prev) => prev.map(x => x.id === u.id ? {
        ...x,
        activation_email_status: data.activation_email_status || 'SENT',
        activation_email_sent_at: data.activation_email_sent_at || x.activation_email_sent_at,
        activation_email_error: null,
      } : x));
      setSuccess(t('awarenessPage.activationEmailSent', { email: u.email }));
    } catch (err) {
      const data = err.response?.data || {};
      const status = data.activation_email_status || 'FAILED';
      const errorType = data.error_type || 'error';
      const errorDetail = data.error_detail || data.error || data.detail || t('awarenessPage.activationEmailGenericError');
      // Aggiorna stato locale utente
      setUserList((prev) => prev.map(x => x.id === u.id ? {
        ...x,
        activation_email_status: status,
        activation_email_error: errorType ? `${errorType}: ${errorDetail}` : errorDetail,
      } : x));
      // Apri modale con il dettaglio dell'errore
      setActivationModal({ user: u, status, error_type: errorType, error_detail: errorDetail });
      setError(t('awarenessPage.activationEmailError', { email: u.email }));
    } finally {
      setSendingActivationId(null);
    }
  }

  function openActivationErrorModal(u) {
    const raw = u.activation_email_error || '';
    let errorType = 'error';
    let errorDetail = raw;
    const idx = raw.indexOf(':');
    if (idx > 0) {
      errorType = raw.slice(0, idx).trim();
      errorDetail = raw.slice(idx + 1).trim();
    }
    setActivationModal({ user: u, status: u.activation_email_status, error_type: errorType, error_detail: errorDetail });
  }

  function handleEditUser(user) {
    setEditingUser(user);
    setUserForm({
      nome: user.nome || '',
      cognome: user.cognome || '',
      email: user.email || '',
      qualifica: user.qualifica || '',
      reparto: user.reparto || '',
      is_vip: user.is_vip || false,
      is_active: user.is_active !== false, // default true
      lingua: user.lingua || 'it',
      stato: user.stato || 'Non iniziato'
    });
    // Apri solo il modal e nascondi il form superiore per evitare che i campi siano visibili sotto il modal
    setIsFormExpanded(false);
    setIsEditModalOpen(true);
  }

  function handleCancelEdit() {
    setEditingUser(null);
    setIsEditModalOpen(false);
    setUserForm({
      nome: '',
      cognome: '',
      email: '',
      qualifica: '',
      reparto: '',
      is_vip: false,
      is_active: true,
      lingua: 'it',
      stato: 'Non iniziato'
    });
    // Ripristina il form principale quando si chiude il modal
    setIsFormExpanded(true);
  }

  function handleResetForm() {
    // Resetta i campi del form agli stati iniziali
    setUserForm({
      nome: '',
      cognome: '',
      email: '',
      qualifica: '',
      reparto: '',
      is_vip: false,
      is_active: true,
      lingua: 'it',
      stato: 'Non iniziato'
    });
    setEditingUser(null);
    setIsEditModalOpen(false);
    setError('');
    setUploadFileName('');
  }

  async function handleDownloadCSV() {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await apiClient.get(API_ENDPOINTS.awarenessUsersExportCSV, {
        params,
        responseType: 'blob' // Importante per il download di file
      });

      // Crea un URL per il blob e simula il click per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'awareness_users_list.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('awarenessPage.errorDownloadCSV'));
      console.error('Error downloading CSV:', err);
    }
  }

  async function handleDownloadTemplate() {
    const csvContent = `nome,cognome,email,reparto,qualifica,is_vip,is_active,lingua
Mario,Rossi,mario.rossi@azienda.it,Sviluppo Software,Senior Developer,false,true,it
Luca,Bianchi,luca.bianchi@azienda.it,Risorse Umane,HR Manager,true,true,it
Anna,Verdi,anna.verdi@azienda.it,Amministrazione,Controller,false,true,en`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_utenti_awareness.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    // If seats are enabled and no remaining slots, block upload early
    if (seatsInfo.awareness_enabled && seatsInfo.remaining <= 0) {
      setError(t('awarenessPage.importSeatsLimit'));
      return;
    }
    setUploadFileName(file.name);
    setIsImporting(true);

    try {
      const text = await file.text();
      const users = parseCSV(text);

      if (users.length === 0) {
        setError(t('awarenessPage.noValidUserInCSV'));
        setIsImporting(false);
        return;
      }

      // Importa automaticamente tutti gli utenti
      let successCount = 0;
      let errorCount = 0;

      for (const userData of users) {
        try {
          const response = await apiClient.post(API_ENDPOINTS.awarenessUsers, userData);
          setUserList((prev) => [...prev, response.data]);
          successCount++;
        } catch (err) {
          console.error('Error importing user:', userData, err);
          // Se l'errore è dovuto al limite posti, mostriamo l'errore e interrompiamo l'import
          const data = err.response?.data;
          if (data && (data.ordine || data.detail)) {
            let message = data.ordine ? (Array.isArray(data.ordine) ? data.ordine[0] : data.ordine) : data.detail;
            if (message && message.toLowerCase().includes('limite utenti awareness')) {
              setError(t('awarenessPage.importLimitReached'));
              errorCount++;
              break;
            }
          }
          errorCount++;
        }
      }

      if (successCount > 0) {
        setError('');
        setSuccess(t('awarenessPage.importCompleted', { success: successCount }) + (errorCount > 0 ? ' ' + t('awarenessPage.importCompletedWithErrors', { errors: errorCount }) : ''));
      } else {
        setError(t('awarenessPage.noUsersImported'));
      }

      // Reset del file input
      e.target.value = '';
      setUploadFileName('');

    } catch (err) {
      setError(t('awarenessPage.errorReadingCSV'));
      console.error('Error reading CSV file:', err);
    } finally {
      setIsImporting(false);
    }
  }

  function parseCSV(csvText) {

    // Prova diversi separatori comuni
    const separators = [',', ';', '\t'];
    let bestSeparator = ',';
    let maxColumns = 0;

    // Trova il separatore che produce più colonne nella prima riga
    for (const sep of separators) {
      const firstLineColumns = csvText.split('\n')[0]?.split(sep).length || 0;
      if (firstLineColumns > maxColumns) {
        maxColumns = firstLineColumns;
        bestSeparator = sep;
      }
    }


    const lines = csvText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length < 2) {
      return [];
    }

    const headers = lines[0].split(bestSeparator).map(h => h.trim().toLowerCase());

    const users = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(bestSeparator).map(v => v.trim().replace(/^["']|["']$/g, '')); // Rimuovi virgolette

      if (values.length >= Math.min(3, headers.length)) { // Almeno nome, cognome, email
        const user = {};

        headers.forEach((header, index) => {
          const value = values[index] || '';

          // Mappa i campi CSV ai campi del form
          switch (header) {
            case 'nome':
            case 'name':
            case 'first name':
            case 'firstname':
              user.nome = value;
              break;
            case 'cognome':
            case 'surname':
            case 'lastname':
            case 'last name':
            case 'cognome':
              user.cognome = value;
              break;
            case 'email':
            case 'e-mail':
            case 'mail':
              user.email = value;
              break;
            case 'reparto':
            case 'team':
            case 'gruppo':
            case 'department':
              user.reparto = value;
              break;
            case 'qualifica':
            case 'role':
            case 'position':
            case 'job title':
            case 'titolo':
              user.qualifica = value;
              break;
            case 'is_vip':
            case 'vip':
              // Accetta 'true', '1', 'si', 'yes' come true
              const vipVal = value.toLowerCase();
              user.is_vip = ['true', '1', 'si', 'sì', 'yes'].includes(vipVal);
              break;
            case 'is_active':
            case 'active':
            case 'attivo':
              // Accetta 'false', '0', 'no' come false, altrimenti true
              const activeVal = value.toLowerCase();
              user.is_active = !['false', '0', 'no'].includes(activeVal);
              break;
            case 'lingua':
            case 'language':
            case 'lang':
              // Normalizza a 'it' o 'en'
              const langVal = value.toLowerCase();
              user.lingua = ['en', 'english', 'inglese'].includes(langVal) ? 'en' : 'it';
              break;
            case 'stato':
            case 'status':
              // Normale valori possibili (locale, non inviato al portale)
              const statoVal = value.toLowerCase();
              if (statoVal.includes('complet')) {
                user.stato = 'Completato';
              } else if (statoVal.includes('in corso') || statoVal.includes('in corso') || statoVal.includes('inprogress')) {
                user.stato = 'In corso';
              } else {
                user.stato = 'Non iniziato';
              }
              break;
          }
        });

        // Aggiungi solo se ha i campi obbligatori
        if (user.nome && user.cognome && user.email) {
          users.push(user);
        } else {
        }
      }
    }

    return users;
  }

  const filteredUsers = userList.filter(u =>
    u.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.cognome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sidebar keys
  const SIDEBAR_KEYS = {
    OVERVIEW: 'overview',
    BY_DEPARTMENT: 'byDepartment',
    USER_MANAGEMENT: 'userManagement'
  };

  const [itemSidebarSelected, setItemSidebarSelected] = useState(SIDEBAR_KEYS.OVERVIEW);

  const sidebarItems = [
    { key: SIDEBAR_KEYS.OVERVIEW, label: t('awarenessPage.dashboard') },
    { key: SIDEBAR_KEYS.BY_DEPARTMENT, label: t('awarenessDepartments.navTitle') },
    { key: SIDEBAR_KEYS.USER_MANAGEMENT, label: safeT('UserManagement.title', 'Gestione Utenti') },
  ];

  const bannerConfig = {
    [SIDEBAR_KEYS.OVERVIEW]: {
      title: t('awarenessPage.dashboard'),
      description: t('awarenessPage.dashboardDescription'),
    },
    [SIDEBAR_KEYS.BY_DEPARTMENT]: {
      title: t('awarenessDepartments.title'),
      description: t('awarenessDepartments.pageDescription'),
    },
    [SIDEBAR_KEYS.USER_MANAGEMENT]: {
      title: safeT('UserManagement.title', 'Gestione Utenti'),
      description: safeT('UserManagement.description', 'Gestisci gli utenti per la formazione Awareness Training.'),
    },
  };

  const selectedBanner = bannerConfig[itemSidebarSelected] || {};
  // Defensive: ensure title/description are always strings
  function ensureString(val, fallback = '') {
    if (typeof val === 'object' && val !== null) return fallback;
    return val || fallback;
  }
  const { title: rawTitle, description: rawDescription } = selectedBanner;
  const title = ensureString(rawTitle, '');
  const description = ensureString(rawDescription, '');

  const renderContent = () => {
    try {
      switch (itemSidebarSelected) {
        case SIDEBAR_KEYS.OVERVIEW:
          return (
            <div className="space-y-6">
              {/* Dashboard Awareness Portal */}
              <AwarenessDashboardCard
                onOpenDepartments={() => setItemSidebarSelected(SIDEBAR_KEYS.BY_DEPARTMENT)}
              />
            </div>
          );
        case SIDEBAR_KEYS.BY_DEPARTMENT:
          return (
            <div className="space-y-6">
              <AwarenessByDepartment />
            </div>
          );
        case SIDEBAR_KEYS.USER_MANAGEMENT:
          return (
            <div className="space-y-6">
              {/* Form Inserimento Utente */}
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <button
                  onClick={() => setIsFormExpanded(!isFormExpanded)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">
                        {editingUser ? t('awarenessPage.editUser') : t('awarenessPage.addNewUser')}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {editingUser ? t('awarenessPage.editUserSubtitle') : t('awarenessPage.addUserSubtitle')}
                      </p>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${isFormExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isFormExpanded && (
                  <form onSubmit={handleAddUser} className="p-3 sm:p-6 border-t bg-gray-50 awareness-form-container">
                    {/* Error / Success banner */}
                    {error && (
                      <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 flex items-start gap-3">
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-9.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM10 13a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                        <div className="text-sm">{error}</div>
                      </div>
                    )}
                    {success && (
                      <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-800 flex items-start gap-3">
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path d="M16.707 5.293a1 1 0 00-1.414-1.414L9 10.172 6.707 7.879a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l7-7z"/></svg>
                        <div className="text-sm">{success}</div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {/* Nome */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('awarenessPage.firstName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          required
                          name="nome"
                          placeholder="Es. Mario"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors"
                          value={userForm.nome}
                          onChange={handleFormChange}
                        />
                      </div>
                      {/* Cognome */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('awarenessPage.lastName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          required
                          name="cognome"
                          placeholder="Es. Rossi"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors"
                          value={userForm.cognome}
                          onChange={handleFormChange}
                        />
                      </div>
                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('awarenessPage.email')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          required
                          name="email"
                          type="email"
                          placeholder="mario.rossi@azienda.it"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors"
                          value={userForm.email}
                          onChange={handleFormChange}
                        />
                      </div>
                      {/* Reparto */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.department')}</label>
                        <input
                          name="reparto"
                          placeholder="Es. Sviluppo Software"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors"
                          value={userForm.reparto}
                          onChange={handleFormChange}
                        />
                      </div>
                      {/* Qualifica */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.qualification')}</label>
                        <input
                          name="qualifica"
                          placeholder="Es. Senior Developer"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors"
                          value={userForm.qualifica}
                          onChange={handleFormChange}
                        />
                      </div>
                      {/* Lingua */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.language')}</label>
                        <select
                          name="lingua"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors"
                          value={userForm.lingua}
                          onChange={handleFormChange}
                        >
                          <option value="it">{t('awarenessPage.languageItalian')}</option>
                          <option value="en">{t('awarenessPage.languageEnglish')}</option>
                        </select>
                      </div>
                      {/* Checkbox VIP e Attivo */}
                      <div className="flex items-end gap-6 pb-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_vip"
                            checked={userForm.is_vip}
                            onChange={handleFormChange}
                            className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                          />
                          <span className="text-sm font-medium text-gray-700">VIP</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={userForm.is_active}
                            onChange={handleFormChange}
                            className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                          />
                          <span className="text-sm font-medium text-gray-700">{t('awarenessPage.activeLabel')}</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t">
                        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        {/* Import CSV Section */}
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="csv-upload"
                          />
                          <label
                            htmlFor="csv-upload"
                            className="flex items-center gap-2 px-4 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            {t('awarenessPage.importCSV')}
                          </label>
                          {uploadFileName && (
                            <span className="text-sm text-gray-600">
                              File: {uploadFileName}
                            </span>
                          )}
                          {isImporting && (
                            <div className="flex items-center gap-2 text-blue-600">
                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              {t('awarenessPage.importingInProgress')}
                            </div>
                          )}
                          {/* Seats info */}
                          {seatsInfo.awareness_enabled && (
                            <div className="text-sm text-gray-600 ml-3">
                              {t('awarenessPage.seatsAvailable')}: <strong>{seatsInfo.remaining}</strong> / {seatsInfo.awareness_seats}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleDownloadTemplate}
                          className="flex items-center gap-2 px-4 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg shadow-sm transition-colors"
                          disabled={seatsInfo.awareness_enabled && seatsInfo.remaining <= 0}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {t('awarenessPage.templateCSV')}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingUser && (
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg shadow-sm transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {t('awarenessPage.cancel')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleResetForm}
                          className="flex items-center gap-2 px-4 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg shadow-sm transition-colors"
                        >
                          {t('awarenessPage.reset')}
                        </button>
                        <button
                          type="submit"
                          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={editingUser ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M12 6v6m0 0v6m0-6h6m-6 0H6"} />
                          </svg>
                          {editingUser ? t('awarenessPage.editUser') : t('awarenessPage.addUser')}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>

              {/* Lista Utenti */}
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="px-3 sm:px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{t('awarenessPage.userList')}</h3>
                        <p className="text-sm text-gray-500">{t('awarenessPage.registeredUsers', { count: userList.length })}</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                      <div className="relative w-full sm:w-auto">
                        <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          placeholder={t('awarenessPage.searchUser')}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-colors w-full sm:w-64"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <button
                        onClick={handleDownloadCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold rounded-lg shadow-sm transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('awarenessPage.downloadCSV')}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('awarenessPage.user')}</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('awarenessPage.contact')}</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('awarenessPage.deptQualification')}</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('awarenessPage.completion')}</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('awarenessPage.activationEmail')}</th>
                        <th className="px-2 sm:px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('awarenessPage.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </div>
                              <p className="text-gray-500 font-medium">{t('awarenessPage.noUserFound')}</p>
                              <p className="text-sm text-gray-400 mt-1">{t('awarenessPage.addFirstUser')}</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-yellow-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center text-black font-semibold relative">
                                  {u.nome.charAt(0)}{u.cognome.charAt(0)}
                                  {u.is_vip && (
                                    <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[10px] px-1 rounded">VIP</span>
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{u.nome} {u.cognome}</p>
                                  <p className="text-sm text-gray-500">{u.qualifica || t('awarenessPage.notSpecified')}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-gray-900">{u.email}</p>
                              <p className="text-sm text-gray-500">
                                {(() => {
                                  const st = u.account_status;
                                  if (st === 'attivo') return <span className="text-green-600">● {t('awarenessPage.accountActive')}</span>;
                                  if (st === 'in_attesa') return <span className="text-amber-600">● {t('awarenessPage.accountPending')}</span>;
                                  if (st === 'disattivato') return <span className="text-red-600">● {t('awarenessPage.accountInactive')}</span>;
                                  return u.is_active !== false
                                    ? <span className="text-green-600">● {t('awarenessPage.activeStatus')}</span>
                                    : <span className="text-red-600">● {t('awarenessPage.deactivatedStatus')}</span>;
                                })()}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-gray-900">{u.reparto || '-'}</p>
                              <p className="text-sm text-gray-500">{u.qualifica || '-'}</p>
                            </td>
                            <td className="px-6 py-4">
                              {(() => {
                                const pct = normalizeCompletion(u.training_completion);
                                const color = pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-blue-500' : 'bg-gray-300';
                                return (
                                  <div className="flex items-center gap-2 min-w-[110px]">
                                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                      <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }}></div>
                                    </div>
                                    <span className="text-xs font-medium text-gray-700 w-9 text-right">{pct}%</span>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4">
                              {(() => {
                                // Assunto: se non ci sono informazioni, l'email risulta inviata
                                // (la verità è smb-awareness, dove gli utenti risultano gestiti).
                                const raw = u.activation_email_status || 'SENT';
                                const st = raw === 'NOT_SENT' ? 'SENT' : raw;
                                const map = {
                                  SENT: { cls: 'bg-green-100 text-green-800', dot: 'bg-green-600', label: t('awarenessPage.activationSent') },
                                  FAILED: { cls: 'bg-red-100 text-red-800', dot: 'bg-red-600', label: t('awarenessPage.activationFailed') },
                                  QUEUED: { cls: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500', label: t('awarenessPage.activationQueued') },
                                };
                                const cfg = map[st] || map.SENT;
                                const badge = (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${cfg.dot}`}></span>
                                    {cfg.label}
                                  </span>
                                );
                                if (st === 'FAILED') {
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => openActivationErrorModal(u)}
                                      className="focus:outline-none hover:underline"
                                      title={t('awarenessPage.activationShowError')}
                                    >
                                      {badge}
                                    </button>
                                  );
                                }
                                return badge;
                              })()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleSendActivationEmail(u)}
                                  disabled={sendingActivationId === u.id}
                                  className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={t('awarenessPage.sendActivationEmailTitle')}
                                >
                                  {sendingActivationId === u.id ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleEditUser(u)}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title={t('awarenessPage.editTitle')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title={t('awarenessPage.deleteTitle')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        default:
          return <div />;
      }
    } catch (err) {
      console.error('React render error:', err);
      return <div style={{color:'red'}}>{t('awarenessPage.renderError')}: {err && err.message ? err.message : 'Errore sconosciuto'}</div>;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-6 items-stretch">
      <div>
        <DescriptionBanner title={t('awarenessPage.title')} description={t('awarenessPage.description')} />
      </div>

      <div className="flex flex-col md:flex-row bg-[#F9FAFB] min-h-[32rem] md:min-h-[40rem]">
        {/* Sidebar */}
        <div className="w-full md:w-60 bg-white shadow p-3 md:p-5 overflow-x-auto md:overflow-y-auto flex md:flex-col gap-2 md:gap-0" data-tour="awareness-sidebar">
          {sidebarItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setItemSidebarSelected(item.key)}
              className={`
                flex gap-2 mb-3 items-center font-semibold w-full px-2 py-1 text-gray-700 rounded transition-colors
                ${itemSidebarSelected === item.key ? 'bg-yellow-400 text-black' : 'hover:bg-yellow-100 hover:text-black'}`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                {item.key === SIDEBAR_KEYS.OVERVIEW ? (
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"/>
                  </svg>
                ) : item.key === SIDEBAR_KEYS.BY_DEPARTMENT ? (
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V9m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h2a2 2 0 012 2v14zm6 0V5m0 14a2 2 0 002 2h2a2 2 0 002-2V9a2 2 0 00-2-2h-2a2 2 0 00-2 2v10z"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                )}
              </span>
              {item.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col px-2 sm:px-6 py-4 overflow-auto" data-tour="awareness-content">
          <DescriptionBanner title={title} description={description} />
          <div className="flex-1 mt-4">
            {renderContent()}
            <EditUserModal isOpen={isEditModalOpen} onClose={handleCancelEdit}>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {error && (
                  <div className="mb-3 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 flex items-start gap-3">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-9.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM10 13a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                    <div className="text-sm">{error}</div>
                  </div>
                )}
                {success && (
                  <div className="mb-3 p-3 rounded-md bg-green-50 border border-green-200 text-green-800 flex items-start gap-3">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path d="M16.707 5.293a1 1 0 00-1.414-1.414L9 10.172 6.707 7.879a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l7-7z"/></svg>
                    <div className="text-sm">{success}</div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.firstName')}</label>
                    <input name="nome" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.nome} onChange={handleFormChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.lastName')}</label>
                    <input name="cognome" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.cognome} onChange={handleFormChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.email')}</label>
                    <input name="email" type="email" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.email} onChange={handleFormChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.department')}</label>
                    <input name="reparto" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.reparto} onChange={handleFormChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.qualification')}</label>
                    <input name="qualifica" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.qualifica} onChange={handleFormChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.language')}</label>
                    <select name="lingua" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.lingua} onChange={handleFormChange}>
                      <option value="it">{t('awarenessPage.languageItalian')}</option>
                      <option value="en">{t('awarenessPage.languageEnglish')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('awarenessPage.trainingStatus')}</label>
                    <select name="stato" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg" value={userForm.stato} onChange={handleFormChange}>
                      <option value="Non iniziato">{t('awarenessPage.statusNotStarted')}</option>
                      <option value="In corso">{t('awarenessPage.statusInProgress')}</option>
                      <option value="Completato">{t('awarenessPage.statusCompleted')}</option>
                    </select>
                  </div>
                  <div className="flex items-end gap-6 pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_vip"
                        checked={userForm.is_vip}
                        onChange={handleFormChange}
                        className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                      />
                      <span className="text-sm font-medium text-gray-700">VIP</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={userForm.is_active}
                        onChange={handleFormChange}
                        className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('awarenessPage.activeLabel')}</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button type="button" onClick={handleCancelEdit} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg">{t('awarenessPage.cancel')}</button>
                  <button type="submit" className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-black rounded-lg font-semibold">{t('awarenessPage.save')}</button>
                </div>
              </form>
            </EditUserModal>
            <ActivationErrorModal
              data={activationModal}
              onClose={() => setActivationModal(null)}
              t={t}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal per la modifica utente
const EditUserModal = ({ isOpen, onClose, children }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('awarenessPage.editUser')}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

// Modal che mostra il dettaglio dell'errore di invio email di attivazione
const ActivationErrorModal = ({ data, onClose, t }) => {
  if (!data) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('awarenessPage.activationErrorTitle')}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <span className="text-gray-500">{t('awarenessPage.activationErrorUser')}: </span>
              <span className="font-medium text-gray-900">{data.user?.email}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('awarenessPage.activationErrorType')}: </span>
              <span className="font-mono text-red-700">{data.error_type || 'error'}</span>
            </div>
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-800 break-words">
              {data.error_detail || t('awarenessPage.activationEmailGenericError')}
            </div>
            <p className="text-gray-500">
              {t('awarenessPage.activationErrorHint')}
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium"
            >
              {t('awarenessPage.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Awareness;
